import { ref, computed } from "vue";
import type { Ref, ComputedRef } from "vue";
import {
  getRestaurantDateKey,
  getRestaurantDateRangeUtc,
} from "@lemmonhq/blogic";
import { useAuth } from "@app/vue/providers/Auth";
import { TAB_CONFIG } from "../config";
import type { TabValue, ViewMode } from "../config";
import { getCurrentShiftFilters } from "../../../utils/shiftHelpers";
import type { ReservationSchedule } from "../../../utils/shiftHelpers";
import type {
  ReservationRequestListFilters,
  ReservationListFilters,
} from "../data/queryKeys";

const dateKeyToLocalDate = (dateKey: string) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

export function useReservationRequestPageState() {
  const auth = useAuth();

  const restaurantTimeZone = computed(
    () => auth.user.value?.restaurant.timezone || "UTC",
  );

  const reservationSchedule = computed<ReservationSchedule | null>(() => {
    const scheduleSetting = auth.user.value?.restaurant_settings?.find(
      (s: any) => s.key === "reservation_schedule",
    );
    if (scheduleSetting && scheduleSetting.value) {
      try {
        return typeof scheduleSetting.value === "string"
          ? JSON.parse(scheduleSetting.value)
          : scheduleSetting.value;
      } catch (e) {
        console.error("Failed to parse reservation schedule:", e);
        return null;
      }
    }
    return null;
  });

  const activeTab = ref<TabValue>("confirmed");
  const isSearchOpen = ref(false);
  const searchQuery = ref("");
  const getTodayDate = () =>
    dateKeyToLocalDate(
      getRestaurantDateKey(new Date(), restaurantTimeZone.value),
    );
  const selectedDate = ref<Date | null>(getTodayDate());
  const viewMode = ref<ViewMode>("day");

  const handleTabChange = (tab: TabValue) => {
    activeTab.value = tab;
    if (tab === "pending") {
      // Pending requests default to "all" so nothing gets missed, but the
      // date selector stays visible so managers can still filter if they want
      viewMode.value = "all";
      selectedDate.value = getTodayDate();
    } else {
      selectedDate.value = getTodayDate();
      viewMode.value = "day";
    }
  };

  const handleDateChange = (date: Date | null) => {
    selectedDate.value = date;
  };

  const handleClearDate = () => {
    selectedDate.value = null;
  };

  const handleSearchToggle = () => {
    isSearchOpen.value = !isSearchOpen.value;
    if (!isSearchOpen.value) {
      searchQuery.value = "";
    }
  };

  const handleSearchChange = (query: string) => {
    searchQuery.value = query;
  };

  const handleViewModeChange = (mode: ViewMode) => {
    viewMode.value = mode;
  };

  // Derived date range for query filters
  const dateFilters = computed(() => {
    const date = selectedDate.value;
    const tz = restaurantTimeZone.value;
    if (!date) return {} as { date_from?: string; date_to?: string };
    if (viewMode.value === "all")
      return {} as { date_from?: string; date_to?: string };
    const range = getRestaurantDateRangeUtc(date, viewMode.value, tz);
    return { date_from: range.date_from, date_to: range.date_to };
  });

  const isRequestTab = computed(
    () => TAB_CONFIG[activeTab.value].type === "request",
  );

  const requestFilters = computed<ReservationRequestListFilters>(() => {
    const tabCfg = TAB_CONFIG[activeTab.value];
    return {
      status: typeof tabCfg.status === "string" ? tabCfg.status : undefined,
      customer_name: searchQuery.value || undefined,
      ...dateFilters.value,
    };
  });

  const reservationFilters = computed<ReservationListFilters>(() => {
    const tabCfg = TAB_CONFIG[activeTab.value];
    const status = tabCfg.status;
    if (activeTab.value === "seated") {
      const shiftFilters = getCurrentShiftFilters(
        new Date(),
        reservationSchedule.value,
        restaurantTimeZone.value,
      );
      return {
        status:
          typeof status === "string"
            ? [status]
            : (status as string[] | undefined),
        customer_name: searchQuery.value || undefined,
        date_from: shiftFilters.date_from,
        date_to: shiftFilters.date_to,
      };
    }
    return {
      status:
        typeof status === "string"
          ? [status]
          : (status as string[] | undefined),
      customer_name: searchQuery.value || undefined,
      ...dateFilters.value,
    };
  });

  return {
    restaurantTimeZone,
    reservationSchedule,
    activeTab,
    isSearchOpen,
    searchQuery,
    selectedDate,
    viewMode,
    isRequestTab,
    requestFilters,
    reservationFilters,
    handleTabChange,
    handleDateChange,
    handleClearDate,
    handleSearchToggle,
    handleSearchChange,
    handleViewModeChange,
  };
}
